import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Select from "./Select";
import Input from "./Input";
import Checkbox from "./Checkbox";
import PhoneInput from "./PhoneInput";
import { searchContact } from "../services/soar";
import fosterHomeMemberOptionSets from "../../options/fosterhomemember.json";
import contactOptionSets from "../../options/contact.json";
import {
  getFieldDate,
  updateEntity,
  createEntity,
  getFieldName,
} from "../../scripts/helperScripts";

function FosterHomeMemberModal({
  isOpen,
  hideModal,
  fosterHome,
  initialData,
  updateMembers,
}) {
  const [fhMemberData, setFHMemberData] = useState({});
  const [contactData, setContactData] = useState({});
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [contactSearchCompleted, setContactSearchCompleted] = useState(false);
  const [addNewContact, setAddNewContact] = useState(false);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Validation Schema
  const fhMemberSchema = {
    ccx_name: Joi.string().allow(null, ""),
    ccx_fosterhomememberid: Joi.string(),
    ccx_primarycontact: Joi.boolean(),
    ccx_homemembertype: Joi.string().required().label("Home Member Type"),
    ccx_contact: Joi.string().required().label("Contact"),
    sfy_effectivedate: Joi.date()
      .max("now")
      .min(new Date("1/1/1900"))
      .required()
      .label("Effective Date")
      .allow("")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Effective Date cannot be future dated`;
              break;
            case "date.min":
              error.message = `Effective Date cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    sfy_expirationdate: Joi.date()
      .label("Expiration Date")
      .max("now")
      .min(new Date(fhMemberData.sfy_effectivedate))
      .allow("")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Expiration Date cannot be future dated`;
              break;
            case "date.min":
              error.message = `Expiration Date cannot be before Effective Date`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
  };

  const contactSchema = {
    firstName: Joi.string().required().label("First Name"),
    lastName: Joi.string().required().label("Last Name"),
    dateOfBirth: Joi.date()
      .label("Date of Birth")
      .max("now")
      .min(new Date("1/1/1900"))
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Date of Birth cannot be future dated`;
              break;
            case "date.min":
              error.message = `Date of Birth cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      })
      .allow(""),
    sex: Joi.string().label("Sex").allow(""),
    ssn: Joi.string()
      .label("SSN")
      .regex(
        /^(?!(000|666|9))\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/,
        "SSN (xxx-xx-xxxx)"
      )
      .max(11)
      .allow(""),
    homePhone: Joi.string().label("Home Phone").allow(""),
    mobilePhone: Joi.string().label("Mobile Phone").allow(""),
    email: Joi.string()
      .label("Email")
      .regex(/^\S+@\S+\.\S+$/, "Email (abc@def.com)")
      .allow(""),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (initialData) {
      setFHMemberData(initialData);
      setContactOptions([
        { _id: initialData.ccx_contact, name: initialData.ccx_name },
      ]);
    }
  }, [initialData]);

  const handleSave = () => {
    if (addNewContact) {
      const contactObj = {
        firstname: {
          type: "string",
          value: contactData.firstName,
        },
        lastname: {
          type: "string",
          value: contactData.lastName,
        },
      };

      // Foster Parent
      if (
        fhMemberData.ccx_homemembertype === "100000000" ||
        fhMemberData.ccx_homemembertype === "100000001" ||
        fhMemberData.ccx_homemembertype === "803080000"
      )
        contactObj.ccx_contacttype = { type: "optionset", value: 229580001 };

      // Foster Family Member
      if (fhMemberData.ccx_homemembertype === "803080001")
        contactObj.ccx_contacttype = { type: "optionset", value: 229580000 };

      // Foster Home Household Member
      if (fhMemberData.ccx_homemembertype === "803080002")
        contactObj.ccx_contacttype = { type: "optionset", value: 229580002 };

      if (contactData.dateOfBirth && contactData.dateOfBirth !== "")
        contactObj.birthdate = {
          type: "datetime",
          value: new Date(contactData.dateOfBirth),
        };

      if (contactData.sex && contactData.sex !== "")
        contactObj.gendercode = {
          type: "optionset",
          value: parseInt(contactData.sex),
        };

      if (contactData.ssn && contactData.ssn !== "")
        contactObj.ccx_ssn = { type: "string", value: contactData.ssn };

      if (contactData.homePhone && contactData.homePhone !== "")
        contactObj.telephone2 = {
          type: "string",
          value: contactData.homePhone,
        };

      if (contactData.mobilePhone && contactData.mobilePhone !== "")
        contactObj.mobilephone = {
          type: "string",
          value: contactData.mobilePhone,
        };

      if (contactData.email && contactData.email !== "")
        contactObj.emailaddress1 = {
          type: "string",
          value: contactData.email,
        };

      const toastId = toast.loading("Contact is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "contact",
        JSON.stringify(contactObj),
        safyInfo,
        getFieldName(fosterHome.owningbusinessunit).split(" of ")[1]
      ).then((contactResponse) => {
        const contactResult = contactResponse.resultset.result;
        const obj = {
          ccx_contact: {
            type: "entity",
            value: {
              entityName: "contact",
              entityId: contactResult.contactid,
            },
          },
          ccx_name: {
            type: "string",
            value: contactResult.fullname,
          },
          ccx_homemembertype: {
            type: "optionset",
            value: parseInt(fhMemberData.ccx_homemembertype),
          },
          ccx_primarycontact: {
            type: "bool",
            value: fhMemberData.ccx_primarycontact || false,
          },
          ccx_fostercarehome: {
            type: "entity",
            value: {
              entityName: "ccx_fostercarehome",
              entityId: fosterHome.ccx_fostercarehomeid,
            },
          },
          sfy_effectivedate: {
            type: "datetime",
            value: new Date(fhMemberData.sfy_effectivedate),
          },
          sfy_expirationdate: {
            type: "datetime",
            value:
              fhMemberData.sfy_expirationdate !== undefined &&
              fhMemberData.sfy_expirationdate !== ""
                ? new Date(fhMemberData.sfy_expirationdate)
                : null,
          },
        };

        toast.update(toastId, {
          render: "Contact has been created. Creating Foster Home Member...",
        });
        createEntity(
          "ccx_fosterhomemember",
          JSON.stringify(obj),
          safyInfo,
          getFieldName(fosterHome.owningbusinessunit).split(" of ")[1]
        )
          .then(() => {
            toast.update(toastId, {
              render: "Foster Home Member has been created.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
            updateMembers && updateMembers(true);
          })
          .catch(() => {
            toast.update(toastId, {
              render:
                "An error occurred while creating the Foster Home Member.",
              type: toast.TYPE.ERROR,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
          });
      });
    } else {
      const selectedContact = contactOptions.filter(
        (option) => option._id === fhMemberData.ccx_contact
      )[0];

      if (!selectedContact) {
        const errs = { ...errors };
        errs.ccx_contact = "Selected Contact value is invalid...";
        setErrors(errs);
        return;
      }

      const obj = {
        ccx_contact: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: fhMemberData.ccx_contact,
          },
        },
        ccx_name: {
          type: "string",
          value: selectedContact.name,
        },
        ccx_homemembertype: {
          type: "optionset",
          value: parseInt(fhMemberData.ccx_homemembertype),
        },
        ccx_primarycontact: {
          type: "bool",
          value: fhMemberData.ccx_primarycontact || false,
        },
        ccx_fostercarehome: {
          type: "entity",
          value: {
            entityName: "ccx_fostercarehome",
            entityId: fosterHome.ccx_fostercarehomeid,
          },
        },
        sfy_effectivedate: {
          type: "datetime",
          value: new Date(fhMemberData.sfy_effectivedate),
        },
        sfy_expirationdate: {
          type: "datetime",
          value: fhMemberData.sfy_expirationdate
            ? new Date(fhMemberData.sfy_expirationdate)
            : null,
        },
      };

      if (fhMemberData.ccx_fosterhomememberid !== undefined) {
        const toastId = toast.loading(
          "Foster Home Member is being updated...",
          { type: toast.TYPE.INFO }
        );
        updateEntity(
          "ccx_fosterhomemember",
          fhMemberData.ccx_fosterhomememberid,
          JSON.stringify(obj),
          safyInfo
        )
          .then(() => {
            toast.update(toastId, {
              render: "Foster Home Member has been updated.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
            updateMembers && updateMembers(true);
          })
          .catch(() => {
            toast.update(toastId, {
              render:
                "An error occurred while updating the Foster Home Member.",
              type: toast.TYPE.ERROR,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
          });
      } else {
        const toastId = toast.loading(
          "Foster Home Member is being created...",
          { type: toast.TYPE.INFO }
        );
        createEntity(
          "ccx_fosterhomemember",
          JSON.stringify(obj),
          safyInfo,
          getFieldName(fosterHome.owningbusinessunit).split(" of ")[1]
        )
          .then(() => {
            toast.update(toastId, {
              render: "Foster Home Member has been created.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
            updateMembers && updateMembers(true);
          })
          .catch(() => {
            toast.update(toastId, {
              render:
                "An error occurred while creating the Foster Home Member.",
              type: toast.TYPE.ERROR,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
          });
      }
    }
  };

  const handleSaveAndAdd = async () => {
    handleSave();
    resetForm();
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input, fhMemberSchema);

    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
    }

    const data = { ...fhMemberData };
    data[input.name] = input.value;
    setFHMemberData(data);

    setErrors(errs);
  };

  const handleContactChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input, contactSchema);

    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
    }

    const data = { ...contactData };
    data[input.name] = input.value;
    setContactData(data);

    setErrors(errs);
  };

  const handleChecked = ({ currentTarget: input }) => {
    input.value = input.checked;
    const errs = { ...errors };
    const errorMessage = validateProperty(input, fhMemberSchema);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
      const data = { ...fhMemberData };
      data[input.name] = input.checked;
      setFHMemberData(data);
    }

    setErrors(errs);
  };

  const validate = () => {
    if (addNewContact) {
      fhMemberData.ccx_contact = "new";
    }

    const options = { abortEarly: false };
    const { error } = Joi.validate(fhMemberData, fhMemberSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const validateProperty = ({ name, value }, schemaObj) => {
    if (name !== "email" && name !== "ssn") {
      const obj = { [name]: value };
      const schema = { [name]: schemaObj[name] };
      const { error } = Joi.validate(obj, schema);
      return error ? error.details[0].message : null;
    }

    return null;
  };

  const resetForm = () => {
    const resetMemberData = {
      ...fhMemberData,
      ccx_contact: "",
      ccx_primarycontact: false,
      sfy_expirationdate: "",
    };
    delete resetMemberData.ccx_fosterhomememberid;

    setFHMemberData(resetMemberData);
    setContactData({});
    setAddNewContact(false);
    setContactOptions([]);
    setContactSearchCompleted(false);
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const handleClientSearch = async () => {
    setContactSearchCompleted(false);
    const response = await searchContact(
      contactSearchQuery,
      safyInfo.user.safyInfo.BusinessUnitIdName
    );

    if (!isMountedRef.current) {
      return;
    }

    if (!response) {
      setContactSearchCompleted(true);
      return;
    }

    const options = response.map((contact) => {
      return {
        _id: contact.contactid,
        name: `${contact.fullname}${
          contact.birthdate ? " - DOB: " + getFieldDate(contact.birthdate) : ""
        }`,
      };
    });

    setContactOptions(options);
    setContactSearchCompleted(true);
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Foster Home Member</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container">
              <div className="row">
                <div className="col">
                  <Input
                    name="contactSearchQuery"
                    label="Search for Contact"
                    placeholder="Search for Contact..."
                    onChange={(e) =>
                      setContactSearchQuery(e.currentTarget.value)
                    }
                    disabled={addNewContact}
                  />
                </div>
                <div className="col-3">
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: "30px" }}
                    onClick={handleClientSearch}
                    disabled={contactSearchQuery === ""}
                  >
                    Search
                  </button>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Select
                    name="ccx_contact"
                    label="Contact"
                    options={contactOptions}
                    value={fhMemberData.ccx_contact}
                    onChange={handleChange}
                    error={errors.ccx_contact}
                    disabled={!contactSearchCompleted || addNewContact}
                  />
                </div>
                <div className="col-3">
                  {!addNewContact && (
                    <button
                      className={
                        fhMemberData.contact
                          ? "btn btn-secondary"
                          : "btn btn-primary"
                      }
                      style={{ marginTop: "30px" }}
                      onClick={() => setAddNewContact(true)}
                      disabled={!contactSearchCompleted || fhMemberData.contact}
                    >
                      New
                    </button>
                  )}
                  {addNewContact && (
                    <button
                      className="btn btn-danger"
                      style={{ marginTop: "30px" }}
                      onClick={() => setAddNewContact(false)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {addNewContact && (
                <>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="firstName"
                        label="First Name"
                        onChange={handleContactChange}
                        value={contactData.firstName}
                        error={errors.firstName}
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="lastName"
                        label="Last Name"
                        onChange={handleContactChange}
                        value={contactData.lastName}
                        error={errors.lastName}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="dateOfBirth"
                        label="Date of Birth"
                        onChange={handleContactChange}
                        value={contactData.dateOfBirth}
                        error={errors.dateOfBirth}
                        type="date"
                      />
                    </div>
                    <div className="col">
                      <Select
                        name="sex"
                        label="Sex"
                        options={contactOptionSets.gendercode}
                        onChange={handleContactChange}
                        value={contactData.Sex}
                        error={errors.sex}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ssn"
                        label="SSN (include dashes)"
                        onChange={handleContactChange}
                        value={contactData.ssn}
                        error={errors.ssn}
                      />
                    </div>
                    <div className="col">
                      <PhoneInput
                        name="homePhone"
                        label="Home Phone"
                        onChange={handleContactChange}
                        value={contactData.homePhone}
                        error={errors.homePhone}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="email"
                        label="Email"
                        onChange={handleContactChange}
                        value={contactData.email}
                        error={errors.email}
                      />
                    </div>
                    <div className="col">
                      <PhoneInput
                        name="mobilePhone"
                        label="Mobile Phone"
                        onChange={handleContactChange}
                        value={contactData.mobilePhone}
                        error={errors.mobilePhone}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <Select
              name="ccx_homemembertype"
              label="Home Member Type"
              options={fosterHomeMemberOptionSets.ccx_homemembertype}
              value={fhMemberData.ccx_homemembertype}
              onChange={handleChange}
              error={errors.ccx_homemembertype}
              disabled={fhMemberData.ccx_fosterhomememberid}
            />
            <div className="ms-4">
              <Checkbox
                name="ccx_primarycontact"
                label="Primary Contact"
                checked={fhMemberData.ccx_primarycontact}
                onChange={handleChecked}
                error={errors.ccx_primarycontact}
              />
            </div>
            <Input
              name="sfy_effectivedate"
              label="Effective Date"
              type="date"
              value={
                fhMemberData.sfy_effectivedate
                  ? moment
                      .utc(fhMemberData.sfy_effectivedate)
                      .format(moment.HTML5_FMT.DATE)
                  : ""
              }
              onChange={handleChange}
              error={errors.sfy_effectivedate}
              disabled={fhMemberData.ccx_fosterhomememberid}
            />
            <Input
              name="sfy_expirationdate"
              label="Expiration Date"
              type="date"
              value={
                fhMemberData.sfy_expirationdate
                  ? moment
                      .utc(fhMemberData.sfy_expirationdate)
                      .format(moment.HTML5_FMT.DATE)
                  : ""
              }
              onChange={handleChange}
              error={errors.sfy_expirationdate}
            />
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-danger" onClick={handleHideModal}>
              Cancel
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) {
                  handleSave();
                  handleHideModal();
                }
              }}
            >
              Submit
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) handleSaveAndAdd();
              }}
            >
              Submit and Add New
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default FosterHomeMemberModal;
